import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTheme } from "@mui/material";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { tokens } from "../../../utils/theme";
import {
    Panel,
    PanelHeader,
    PanelTitle,
    PanelFilters,
    TableWrapper,
    Table,
    Thead,
    Th,
    Tr,
    Td,
    TdSymbol,
    TdSide,
    TdPnl,
    TdSmall,
    ScrollRail,
    ScrollThumb,
    ScrollRailVertical,
    ScrollThumbVertical,
    Btn,
    BtnIcon
} from "../../../components/SharedPageStyles";
import { TreeView, TreeContainer } from "../../../components/TreeView";
import { FiEye } from "react-icons/fi";
import { MdContentCopy, MdClose } from "react-icons/md";
import {
    calculateSetupScore,
    calculateManagementScore,
    deriveMacroConfidence
} from "../../../utils/technicalCalculator";
import { calculateTradePnL } from "../../../utils/pnlCalculator";
import styled from "@xstyled/styled-components";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useJournalData } from "../../../context/JournalContext";
import { useMarketOverviewData, isStatusRow } from "../../../context/MarketOverviewContext";
import { copyTableAsJSON } from "../../../utils/clipboardUtils";
import CustomSelect from "../../../components/CustomSelect";
import { MARKET_STRUCTURE_DATA } from "../../../constants/marketStructureData";

const TableDropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: ${props => props.isOpen ? 9999 : 1};
`;
const TableDropdownTrigger = styled.div`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? '#141b2d' : '#fcfcfc'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: ${props => props.theme.palette.mode === 'dark' ? '#e0e0e0' : '#333333'};
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
`;
const TableDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  width: max-content;
  background: ${props => props.theme.palette.mode === 'dark' ? '#1b223c' : '#ffffff'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  border-radius: 6px;
  z-index: 9999;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  max-height: 250px;
  overflow-y: auto;

  /* Custom Scrollbar */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
      background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
      border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
      background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
  }
`;
const TableDropdownGroup = styled.div`
  padding: 6px 8px 2px 8px;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  color: ${props => props.theme.palette.mode === 'dark' ? '#4db6ac' : '#0f766e'};
`;
const TableDropdownOption = styled.div`
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 4px;
  color: ${props => props.theme.palette.mode === 'dark' ? '#e0e0e0' : '#333333'};
  background: ${props => props.selected ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.2)' : 'rgba(15,118,110,0.1)') : 'transparent'};

  &:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'};
  }
`;

const EditableInput = styled.input`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
`;

const EditableTextArea = styled.textarea`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  padding-right: 36px; /* Space for arrows */
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  min-height: 60px;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollControls = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  pointer-events: none;
`;

const ScrollButton = styled.div`
  pointer-events: auto;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 2px;
  transition: all 0.2s;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)'};

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.95)'};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

// Section Header Style
const SectionHeader = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'};
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  border-top: 1px solid ${props => tokens(props.theme.palette.mode).grey[800]};
  border-bottom: 1px solid ${props => tokens(props.theme.palette.mode).grey[800]};
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0px; // Spacing handled by containers
  
  // Color coding
  ${props => props.type === 'active' && `
    border-left: 4px solid ${tokens(props.theme.palette.mode).greenAccent[500]};
    color: ${tokens(props.theme.palette.mode).greenAccent[500]};
  `}
  ${props => props.type === 'ready' && `
    border-left: 4px solid ${tokens(props.theme.palette.mode).blueAccent[500]};
    color: ${tokens(props.theme.palette.mode).blueAccent[400]};
  `}
  ${props => props.type === 'done' && `
    border-left: 4px solid ${tokens(props.theme.palette.mode).grey[500]};
    color: ${tokens(props.theme.palette.mode).grey[400]};
  `}
`;



const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  text-align: center;
  color: ${props => props.theme.palette.text.primary};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

const CheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;
  position: relative;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[500]
        : 'transparent'};
  border: 2px solid ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[500]
        : tokens(props.theme.palette.mode).grey[500]};
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}40; 
  }

  &:hover {
     border-color: ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[600]
        : tokens(props.theme.palette.mode).blueAccent[400]};
  }
`;

const Icon = styled.svg`
  fill: none;
  stroke: ${props => props.theme.palette.mode === 'dark' ? '#000' : '#fff'};
  stroke-width: 3px;
  width: 12px;
  height: 12px;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
`;

const CustomCheckbox = ({ checked, indeterminate, onChange, ...props }) => (
    <CheckboxContainer onClick={(e) => {
        e.stopPropagation();
        onChange({ target: { checked: !checked } });
    }}>
        <HiddenCheckbox checked={checked} onChange={onChange} {...props} />
        <StyledCheckbox checked={checked} indeterminate={indeterminate}>
            {indeterminate ? (
                <Icon viewBox="0 0 24 24" visible={true}>
                    <line x1="4" y1="12" x2="20" y2="12" />
                </Icon>
            ) : (
                <Icon viewBox="0 0 24 24" visible={checked}>
                    <polyline points="20 6 9 17 4 12" />
                </Icon>
            )}
        </StyledCheckbox>
    </CheckboxContainer>
);

const TableDropdownCell = ({ value, onChange, options }) => {
    const [localValue, setLocalValue] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                menuRef.current && !menuRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        const handleScroll = (event) => {
            if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target) && menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            window.addEventListener("scroll", handleScroll, true);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [isOpen]);

    const handleToggle = () => {
        if (!isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setCoords({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width
            });
        }
        setIsOpen(!isOpen);
    };

    return (
        <TableDropdownWrapper ref={dropdownRef} onClick={(e) => e.stopPropagation()} isOpen={isOpen}>
            <TableDropdownTrigger onClick={handleToggle}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{localValue || "Select..."}</span>
                <span style={{ fontSize: '10px', opacity: 0.5, marginLeft: '4px' }}>▼</span>
            </TableDropdownTrigger>
            {isOpen && ReactDOM.createPortal(
                <TableDropdownMenu
                    ref={menuRef}
                    style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.width }}
                >
                    {options.map((opt, idx) => {
                        if (opt.group) {
                            return (
                                <React.Fragment key={idx}>
                                    <TableDropdownGroup>{opt.group}</TableDropdownGroup>
                                    {opt.items.map((item, idx2) => (
                                        <TableDropdownOption
                                            key={idx2}
                                            selected={localValue === item.value}
                                            onClick={() => {
                                                setLocalValue(item.value);
                                                onChange(item.value);
                                                setIsOpen(false);
                                            }}
                                        >
                                            {item.label}
                                        </TableDropdownOption>
                                    ))}
                                </React.Fragment>
                            );
                        }
                        return (
                            <TableDropdownOption
                                key={idx}
                                selected={localValue === opt.value}
                                onClick={() => {
                                    setLocalValue(opt.value);
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }}
                            >
                                {opt.label}
                            </TableDropdownOption>
                        );
                    })}
                </TableDropdownMenu>,
                document.body
            )}
        </TableDropdownWrapper>
    );
};

const EditableCell = ({ value, onChange, type = "text", options = [] }) => {
    const [localValue, setLocalValue] = useState(value);
    const [showScroll, setShowScroll] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (textareaRef.current) {
            const { scrollHeight, clientHeight } = textareaRef.current;
            setShowScroll(scrollHeight > clientHeight);
        }
    }, [localValue]);

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handleScroll = (direction) => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop += direction * 20;
        }
    };

    if (type === "select") {
        return <TableDropdownCell value={value} onChange={onChange} options={options} />;
    }

    if (type === "textarea") {
        return (
            <TextAreaWrapper>
                <EditableTextArea
                    ref={textareaRef}
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    onBlur={handleBlur}
                    onClick={(e) => e.stopPropagation()}
                />
                {showScroll && (
                    <ScrollControls>
                        <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(-1); }} title="Scroll Up">
                            <svg viewBox="0 0 24 24">
                                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                            </svg>
                        </ScrollButton>
                        <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(1); }} title="Scroll Down">
                            <svg viewBox="0 0 24 24">
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                            </svg>
                        </ScrollButton>
                    </ScrollControls>
                )}
            </TextAreaWrapper>
        );
    }
    return (
        <EditableInput
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
        />
    );
};

const JournalTab = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [viewMode, setViewMode] = React.useState('detailed'); // 'detailed' | 'minimal'
    const { rows, filteredRows, updateRow, deleteRow, addRow, setAllData, dateFilter, setDateFilter } = useJournalData();
    const [dateSortDirection, setDateSortDirection] = useState('desc'); // 'asc' | 'desc'
    const [assetSortDirection, setAssetSortDirection] = useState(null); // null | 'asc' | 'desc'
    const { rows: marketOverviewRows } = useMarketOverviewData(); // Get Market Overview Data
    const [activeAnalysisTrade, setActiveAnalysisTrade] = useState(null);
    const [activeThesisTrade, setActiveThesisTrade] = useState(null);

    // Generate Asset Options from Market Overview
    const assetOptions = useMemo(() => {
        if (!marketOverviewRows) return [];
        // Extract items that have a Catalyst (Ticker) and map them to options
        // We filter for distinct catalysts
        const catalysts = [...new Set(marketOverviewRows.filter(row => row.Catalyst).map(row => row.Catalyst))];
        return catalysts.map(cat => ({ value: cat, label: cat }));
    }, [marketOverviewRows]);

    const exitReasonOptions = useMemo(() => {
        return MARKET_STRUCTURE_DATA.exit_reasons.categories.map(cat => ({
            group: cat.name,
            items: cat.items.map(item => ({
                value: item.label,
                label: item.label
            }))
        }));
    }, []);

    // Pure Ex-Ante Setup Score calculator (stored in trade.setup_score):
    const computeSetupScore = useCallback((trade) => {
        if (!trade) return null;

        const entryP = parseFloat(trade.entry);
        const slP = parseFloat(trade.sl);
        const tpP = parseFloat(trade.tp);
        const hasSetup = Boolean(entryP && slP && entryP !== slP);
        const rrPlanned = (hasSetup && tpP)
            ? Math.abs(tpP - entryP) / Math.abs(entryP - slP)
            : null;

        const setup = calculateSetupScore(trade.view, rrPlanned);
        const sSetup = setup ? setup.score : 45.0;
        return Number((sSetup / 100).toFixed(2));
    }, []);

    // Formatter / Badge for Catalyst Confidence Score (LLM for Event-Driven, Bias Calculator for Macro)
    const renderConfidenceBadge = useCallback((val, style) => {
        if (val === null || val === undefined || val === '') {
            return <span style={{ color: colors.grey[600], fontSize: '0.75rem' }}>-</span>;
        }
        const num = parseFloat(val);
        if (isNaN(num)) return <span style={{ fontSize: '0.75rem' }}>{val}</span>;
        const pct = num <= 1.0 ? Math.round(num * 100) : Math.round(num);
        const color = pct >= 80 ? '#10b981' : (pct >= 65 ? '#60a5fa' : '#f59e0b');
        const bg = pct >= 80 ? 'rgba(16, 185, 129, 0.15)' : (pct >= 65 ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)');
        const border = pct >= 80 ? 'rgba(16, 185, 129, 0.3)' : (pct >= 65 ? 'rgba(59, 130, 246, 0.3)' : 'rgba(245, 158, 11, 0.3)');
        const decimalStr = num <= 1.0 ? num.toFixed(2) : (num / 100).toFixed(2);

        const isMacro = (style || '').toUpperCase() === 'MACRO';
        const isEventDriven = (style || '').toUpperCase() === 'EVENT-DRIVEN';
        const sourceLabel = isMacro ? 'Macro Bias (Bias Calculator)' : (isEventDriven ? 'Event-Driven (LLM)' : 'Catalyst Confidence');

        return (
            <span
                style={{
                    display: 'inline-block',
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color,
                    background: bg,
                    border: `1px solid ${border}`,
                    cursor: 'default'
                }}
                title={`${sourceLabel}: ${decimalStr} (${pct}%)`}
            >
                {decimalStr}
            </span>
        );
    }, [colors.grey]);


    // Helper to get or derive confidence score: LLM for Event-Driven, Bias Calculator for Macro
    const getEffectiveConfidence = useCallback((trade) => {
        if (!trade) return null;
        if (trade.confidenceScore !== null && trade.confidenceScore !== undefined && trade.confidenceScore !== '') {
            return trade.confidenceScore;
        }
        const isMacro = (trade.style || '').toUpperCase() === 'MACRO';
        if (isMacro && trade.asset) {
            const moData = marketOverviewRows.find(r => r.Catalyst === trade.asset && isStatusRow(r))
                || marketOverviewRows.find(r => r.Catalyst === trade.asset);
            if (moData) {
                return deriveMacroConfidence(moData);
            }
        }
        return trade.confidenceScore;
    }, [marketOverviewRows]);

    const handleAssetChange = (filteredIndex, newAsset) => {
        // Find the actual index and row
        const rowToUpdate = filteredRows[filteredIndex];
        const actualIndex = rows.findIndex(r => r === rowToUpdate);

        if (actualIndex !== -1) {
            const currentRow = rows[actualIndex];

            // 1. Look for corresponding Market Overview data (source of bias_calculator)
            const moData = marketOverviewRows.find(r => r.Catalyst === newAsset && isStatusRow(r))
                || marketOverviewRows.find(r => r.Catalyst === newAsset);

            // 2. Look for existing event-driven trade template from Database rows for this asset
            const dbTrade = rows.find(r =>
                String(r.asset || '').toUpperCase() === String(newAsset || '').toUpperCase() &&
                (r.style || '').toUpperCase() === 'EVENT-DRIVEN' &&
                r.confidenceScore !== null && r.confidenceScore !== undefined
            );

            // 3. Determine trade style: preserve currentRow.style or default
            const targetStyle = (currentRow.style || (dbTrade ? 'EVENT-DRIVEN' : 'MACRO')).toUpperCase();

            // 4. Resolve confidence score:
            // - If MACRO: derived from bias_calculator (moData)
            // - If EVENT-DRIVEN: from LLM (dbTrade or currentRow)
            let resolvedConfidence = null;
            if (targetStyle === 'MACRO') {
                resolvedConfidence = deriveMacroConfidence(moData);
            } else if (targetStyle === 'EVENT-DRIVEN') {
                if (dbTrade && dbTrade.confidenceScore !== undefined && dbTrade.confidenceScore !== null) {
                    resolvedConfidence = dbTrade.confidenceScore;
                } else if (currentRow.confidenceScore !== undefined && currentRow.confidenceScore !== null) {
                    resolvedConfidence = currentRow.confidenceScore;
                } else {
                    resolvedConfidence = deriveMacroConfidence(moData);
                }
            } else {
                resolvedConfidence = currentRow.confidenceScore !== undefined && currentRow.confidenceScore !== null
                    ? currentRow.confidenceScore
                    : deriveMacroConfidence(moData);
            }

            const updatedRow = {
                ...currentRow,
                asset: newAsset,
                side: (dbTrade && dbTrade.side) ? dbTrade.side : currentRow.side,
                style: targetStyle,
                catalystType: (dbTrade && (dbTrade.catalyst_type || dbTrade.catalystType)) ? (dbTrade.catalyst_type || dbTrade.catalystType) : (currentRow.catalystType || (moData ? moData.Category : '')),
                catalystName: (dbTrade && (dbTrade.catalyst_name || dbTrade.catalystName)) ? (dbTrade.catalyst_name || dbTrade.catalystName) : (currentRow.catalystName || (moData ? moData.Catalyst : '')),
                thesis: (dbTrade && dbTrade.thesis) ? dbTrade.thesis : (moData ? moData.Headline : currentRow.thesis),
                scenario: (dbTrade && dbTrade.scenario) ? dbTrade.scenario : (moData ? moData.Tag : currentRow.scenario),
                confidenceScore: resolvedConfidence
            };

            updateRow(actualIndex, updatedRow);
        }
    };

    const [isEditMode, setIsEditMode] = useState(false);
    const [originalDataSnapshot, setOriginalDataSnapshot] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    // const [editingCell, setEditingCell] = useState(null);

    // Filter logic is now handled in JournalContext

    // Context Menu State
    const [rowsToDelete, setRowsToDelete] = useState(null);

    // Refs for scrolling
    const tableWrapperRef = useRef(null);
    const scrollRailXRef = useRef(null);
    const scrollRailYRef = useRef(null);

    // Horizontal Scroll State
    const [thumbWidth, setThumbWidth] = useState("38%");
    const [thumbLeft, setThumbLeft] = useState("0%");
    const [showScrollX, setShowScrollX] = useState(false);
    const isDraggingX = useRef(false);
    const startX = useRef(0);
    const startScrollLeft = useRef(0);

    // Vertical Scroll State
    const [thumbHeight, setThumbHeight] = useState("38%");
    const [thumbTop, setThumbTop] = useState("0%");
    const [showScrollY, setShowScrollY] = useState(false);
    const isDraggingY = useRef(false);
    const startY = useRef(0);
    const startScrollTop = useRef(0);

    // Scroll Logic
    const handleScroll = () => {
        if (tableWrapperRef.current) {
            if (isDraggingX.current || isDraggingY.current) return;

            const {
                scrollLeft, scrollWidth, clientWidth,
                scrollTop, scrollHeight, clientHeight
            } = tableWrapperRef.current;

            const maxScrollLeft = scrollWidth - clientWidth;
            if (maxScrollLeft > 0) {
                const scrollPercentageX = (scrollLeft / maxScrollLeft) * 100;
                const maxLeft = 100 - parseFloat(thumbWidth);
                const left = (scrollPercentageX / 100) * maxLeft;
                setThumbLeft(`${left}% `);
            }

            const maxScrollTop = scrollHeight - clientHeight;
            if (maxScrollTop > 0) {
                const scrollPercentageY = (scrollTop / maxScrollTop) * 100;
                const maxTop = 100 - parseFloat(thumbHeight);
                const top = (scrollPercentageY / 100) * maxTop;
                setThumbTop(`${top}% `);
            }
        }
    };

    useEffect(() => {
        const checkScroll = () => {
            if (tableWrapperRef.current) {
                const { scrollWidth, clientWidth, scrollHeight, clientHeight } = tableWrapperRef.current;

                const hasScrollX = scrollWidth > clientWidth;
                setShowScrollX(hasScrollX);
                if (hasScrollX) {
                    const widthPerc = (clientWidth / scrollWidth) * 100;
                    const finalWidth = Math.max(widthPerc, 10);
                    setThumbWidth(`${finalWidth}% `);
                }

                const hasScrollY = scrollHeight > clientHeight;
                setShowScrollY(hasScrollY);
                if (hasScrollY) {
                    const heightPerc = (clientHeight / scrollHeight) * 100;
                    const finalHeight = Math.max(heightPerc, 10);
                    setThumbHeight(`${finalHeight}% `);
                }
            }
        };

        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [viewMode, rows]);

    // Edit & Context Menu Logic
    const handleEditModeToggle = () => {
        if (!isEditMode) {
            setOriginalDataSnapshot(JSON.parse(JSON.stringify(rows)));
            setIsEditMode(true);
            setSelectedRows(new Set());
        } else {
            setIsEditMode(false);
            setOriginalDataSnapshot(null);
            setSelectedRows(new Set());
        }
    };

    const handleSave = () => {
        setIsEditMode(false);
        setOriginalDataSnapshot(null);
        setSelectedRows(new Set());
    };

    const handleCancel = () => {
        if (originalDataSnapshot) {
            setAllData(originalDataSnapshot);
        }
        setIsEditMode(false);
        setOriginalDataSnapshot(null);
        setSelectedRows(new Set());
    };

    const handleCellUpdate = (filteredIndex, field, newValue) => {
        // Find the actual index in the main rows array
        const rowToUpdate = filteredRows[filteredIndex];
        const actualIndex = rows.findIndex(r => r === rowToUpdate);

        if (actualIndex !== -1) {
            let updatedRow = { ...rows[actualIndex], [field]: newValue };

            // When editing confidenceScore manually in edit mode, parse cleanly
            if (field === 'confidenceScore') {
                if (newValue === '' || newValue === null || newValue === undefined) {
                    updatedRow.confidenceScore = null;
                } else {
                    const parsed = parseFloat(String(newValue).replace('%', '').trim());
                    if (!isNaN(parsed)) {
                        updatedRow.confidenceScore = parsed > 1.0 ? Number((parsed / 100).toFixed(2)) : parsed;
                    }
                }
            }

            // When updating style, automatically derive appropriate confidence score
            if (field === 'style') {
                const targetStyle = String(newValue || '').toUpperCase();
                if (targetStyle === 'MACRO') {
                    const moData = marketOverviewRows.find(r => r.Catalyst === updatedRow.asset && isStatusRow(r))
                        || marketOverviewRows.find(r => r.Catalyst === updatedRow.asset);
                    updatedRow.confidenceScore = deriveMacroConfidence(moData);
                } else if (targetStyle === 'EVENT-DRIVEN') {
                    const dbTrade = rows.find(r =>
                        String(r.asset || '').toUpperCase() === String(updatedRow.asset || '').toUpperCase() &&
                        (r.style || '').toUpperCase() === 'EVENT-DRIVEN' &&
                        r.confidenceScore !== null && r.confidenceScore !== undefined
                    );
                    if (dbTrade) {
                        updatedRow.confidenceScore = dbTrade.confidenceScore;
                    }
                }
            }

            // Dynamic recalculation: whenever entry, sl, tp, or view is edited, recalculate Setup Score:
            if (['entry', 'sl', 'tp', 'view'].includes(field)) {
                const s = computeSetupScore(updatedRow);
                updatedRow.setupScore = s;
                updatedRow.setup_score = s;
            }

            // Dynamic recalculation: whenever exit, exitReason, entry, sl, tp, or side is edited, recalculate Management Score:
            if (['exit', 'exitReason', 'entry', 'sl', 'tp', 'side'].includes(field)) {
                if (updatedRow.exit) {
                    const mgmt = calculateManagementScore(
                        updatedRow.exit,
                        updatedRow.entry,
                        updatedRow.sl,
                        updatedRow.tp,
                        updatedRow.side,
                        updatedRow.exitReason || updatedRow.exit_reason
                    );
                    if (mgmt && mgmt.grade !== 'PENDING') {
                        const m = Number((mgmt.score / 100).toFixed(2));
                        updatedRow.managementScore = m;
                        updatedRow.management_score = m;
                    }
                }
            }

            // Dynamic recalculation: whenever entry, exit, size, side, scenario, or asset is edited,
            // automatically calculate PnL ($) and PnL (%)
            if (['entry', 'exit', 'size', 'side', 'scenario', 'asset'].includes(field)) {
                const calculated = calculateTradePnL(updatedRow);
                if (calculated.pnl !== null) {
                    updatedRow.pnl = calculated.pnl;
                }
                if (calculated.pnlPercent !== null) {
                    updatedRow.pnlPercent = calculated.pnlPercent;
                }
            }

            updateRow(actualIndex, updatedRow);
        }
    };

    const handleRowClick = (trade) => {
        if (isEditMode) {
            const newSelected = new Set(selectedRows);
            if (newSelected.has(trade)) {
                newSelected.delete(trade);
            } else {
                newSelected.add(trade);
            }
            setSelectedRows(newSelected);
        }
    };

    const handleDeleteSelected = () => {
        if (selectedRows.size > 0) {
            setRowsToDelete(Array.from(selectedRows));
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(new Set(filteredRows));
        } else {
            setSelectedRows(new Set());
        }
    };

    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        // If moved to a different group (Status Change)
        if (destination.droppableId !== source.droppableId) {
            const newStatus = destination.droppableId;

            // Find trade index in main rows
            // draggableId was set to trade.id (which is unique)
            // But we need to handle if IDs are not numbers or if they are strings in draggableId
            // The draggableId is String(trade.id).
            const tradeIndex = rows.findIndex(r => String(r.id) === draggableId);

            if (tradeIndex !== -1) {
                const currentTrade = rows[tradeIndex];
                let updatedRow = { ...currentTrade, status: newStatus };

                if (newStatus === 'Done') {
                    // Automatically compute Management Score if exit exists
                    if (updatedRow.exit) {
                        const mgmt = calculateManagementScore(
                            updatedRow.exit,
                            updatedRow.entry,
                            updatedRow.sl,
                            updatedRow.tp,
                            updatedRow.side,
                            updatedRow.exitReason || updatedRow.exit_reason
                        );
                        if (mgmt && mgmt.grade !== 'PENDING') {
                            const m = Number((mgmt.score / 100).toFixed(2));
                            updatedRow.managementScore = m;
                            updatedRow.management_score = m;
                        }
                    }

                    // Automatically compute PnL ($) and PnL (%) if entry & exit exist
                    const pnlRes = calculateTradePnL(updatedRow);
                    if (pnlRes.pnl !== null && (!updatedRow.pnl || updatedRow.pnl === '0')) {
                        updatedRow.pnl = pnlRes.pnl;
                    }
                    if (pnlRes.pnlPercent !== null && (!updatedRow.pnlPercent || updatedRow.pnlPercent === '0%')) {
                        updatedRow.pnlPercent = pnlRes.pnlPercent;
                    }
                }

                updateRow(tradeIndex, updatedRow);
            }
        }
    };

    const confirmDelete = () => {
        if (rowsToDelete && rowsToDelete.length > 0) {
            const indicesToDelete = rowsToDelete
                .map(trade => rows.findIndex(r => r === trade))
                .filter(idx => idx !== -1)
                .sort((a, b) => b - a);

            indicesToDelete.forEach(index => {
                deleteRow(index);
            });

            setRowsToDelete(null);
            setSelectedRows(new Set());
        }
    };

    // Drag Handlers
    const handleMouseMove = (e) => {
        if (isDraggingX.current && tableWrapperRef.current && scrollRailXRef.current) {
            e.preventDefault();
            const { scrollWidth, clientWidth } = tableWrapperRef.current;
            const railWidth = scrollRailXRef.current.clientWidth;
            const thumbWidthPx = (parseFloat(thumbWidth) / 100) * railWidth;
            const maxScrollLeft = scrollWidth - clientWidth;
            const availableRail = railWidth - thumbWidthPx;
            const deltaX = e.clientX - startX.current;
            const scrollRatio = maxScrollLeft / availableRail;
            tableWrapperRef.current.scrollLeft = startScrollLeft.current + (deltaX * scrollRatio);

            // Update thumb position immediately for smoothness
            const scrollPercentageX = (tableWrapperRef.current.scrollLeft / maxScrollLeft) * 100;
            const maxLeft = 100 - parseFloat(thumbWidth);
            const left = (scrollPercentageX / 100) * maxLeft;
            setThumbLeft(`${left}% `);
        }
        if (isDraggingY.current && tableWrapperRef.current && scrollRailYRef.current) {
            e.preventDefault();
            const { scrollHeight, clientHeight } = tableWrapperRef.current;
            const railHeight = scrollRailYRef.current.clientHeight;
            const thumbHeightPx = (parseFloat(thumbHeight) / 100) * railHeight;
            const maxScrollTop = scrollHeight - clientHeight;
            const availableRail = railHeight - thumbHeightPx;
            const deltaY = e.clientY - startY.current;
            const scrollRatio = maxScrollTop / availableRail;
            tableWrapperRef.current.scrollTop = startScrollTop.current + (deltaY * scrollRatio);

            // Update thumb position immediately
            const scrollPercentageY = (tableWrapperRef.current.scrollTop / maxScrollTop) * 100;
            const maxTop = 100 - parseFloat(thumbHeight);
            const top = (scrollPercentageY / 100) * maxTop;
            setThumbTop(`${top}% `);
        }
    };

    const handleMouseUp = () => {
        isDraggingX.current = false;
        isDraggingY.current = false;
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDownX = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDraggingX.current = true;
        startX.current = e.clientX;
        startScrollLeft.current = tableWrapperRef.current.scrollLeft;
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDownY = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDraggingY.current = true;
        startY.current = e.clientY;
        startScrollTop.current = tableWrapperRef.current.scrollTop;
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>Latest trades</PanelTitle>
                <PanelFilters>
                    <div style={{ display: 'flex', background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)', borderRadius: '999px', padding: '2px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'} ` }}>
                        <div
                            onClick={() => setViewMode('minimal')}
                            style={{
                                padding: '4px 12px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                background: viewMode === 'minimal' ? (theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.2)' : colors.primary[100]) : 'transparent',
                                color: viewMode === 'minimal' ? (theme.palette.mode === 'dark' ? '#80cbc4' : colors.primary[700]) : colors.grey[400],
                                fontWeight: viewMode === 'minimal' ? 600 : 400
                            }}
                        >
                            Minimal
                        </div>
                        <div
                            onClick={() => setViewMode('detailed')}
                            style={{
                                padding: '4px 12px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                background: viewMode === 'detailed' ? (theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.2)' : colors.primary[100]) : 'transparent',
                                color: viewMode === 'detailed' ? (theme.palette.mode === 'dark' ? '#80cbc4' : colors.primary[700]) : colors.grey[400],
                                fontWeight: viewMode === 'detailed' ? 600 : 400
                            }}
                        >
                            Detailed
                        </div>
                    </div>
                    <CustomSelect
                        value={dateFilter}
                        onChange={setDateFilter}
                        options={[
                            "All",
                            "Today",
                            "Last 7 days",
                            "Last 30 days",
                            "Last 3 months",
                            "Last 6 months",
                            "Last year"
                        ]}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '12px', alignItems: 'center' }}>
                        <BtnIcon onClick={() => copyTableAsJSON(filteredRows)} title="Copy Table JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                        {isEditMode ? (
                            <>
                                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                                <Btn primary onClick={addRow} style={{ width: '30px', height: '30px', minWidth: '30px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</Btn>
                                <Btn
                                    danger
                                    onClick={handleDeleteSelected}
                                    disabled={selectedRows.size === 0}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        minWidth: '30px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: selectedRows.size === 0 ? 0.5 : 1,
                                        cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer',
                                        background: selectedRows.size > 0 ? '#d32f2f' : undefined,
                                        borderColor: selectedRows.size > 0 ? '#b71c1c' : undefined,
                                        color: selectedRows.size > 0 ? '#fff' : undefined
                                    }}
                                >
                                    - {selectedRows.size > 0 && `(${selectedRows.size})`}
                                </Btn>
                                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                                <Btn primary onClick={handleSave}>Save</Btn>
                                <Btn danger onClick={handleCancel}>Cancel</Btn>
                            </>
                        ) : (
                            <BtnIcon onClick={handleEditModeToggle} title="Edit Mode">
                                ✎
                            </BtnIcon>
                        )}
                    </div>
                </PanelFilters>
            </PanelHeader>

            <TableWrapper ref={tableWrapperRef} onScroll={handleScroll}>
                <Table>
                    <Thead>
                        <tr>
                            {isEditMode && (
                                <Th style={{ width: '40px', textAlign: 'center' }}>
                                    <CustomCheckbox
                                        checked={filteredRows.length > 0 && selectedRows.size === filteredRows.length}
                                        indeterminate={selectedRows.size > 0 && selectedRows.size < filteredRows.length}
                                        onChange={handleSelectAll}
                                    />
                                </Th>
                            )}
                            {isEditMode && <Th style={{ width: '50px' }}>ID</Th>}
                            <Th
                                style={{ minWidth: '120px', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setDateSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    Date
                                    {dateSortDirection === 'desc'
                                        ? <ArrowDownward style={{ fontSize: '14px', opacity: 0.7 }} />
                                        : <ArrowUpward style={{ fontSize: '14px', opacity: 0.7 }} />
                                    }
                                </div>
                            </Th>
                            <Th
                                style={{ minWidth: '100px', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setAssetSortDirection(prev => {
                                    if (prev === null) return 'asc';
                                    if (prev === 'asc') return 'desc';
                                    return null;
                                })}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    Asset
                                    {assetSortDirection === 'desc' && <ArrowDownward style={{ fontSize: '14px', opacity: 0.7 }} />}
                                    {assetSortDirection === 'asc' && <ArrowUpward style={{ fontSize: '14px', opacity: 0.7 }} />}
                                </div>
                            </Th>
                            <Th style={{ minWidth: '80px' }}>Side</Th>

                            {viewMode === 'detailed' && <Th style={{ minWidth: '100px' }}>Style</Th>}
                            {viewMode === 'detailed' && (
                                <>
                                    <Th style={{ minWidth: '160px' }}>Catalyst</Th>
                                    <Th style={{ minWidth: isEditMode ? '260px' : '60px', textAlign: isEditMode ? 'left' : 'center' }}>Thesis</Th>
                                    <Th style={{ minWidth: '120px' }}>Scenario</Th>
                                    <Th style={{ minWidth: '60px' }} title="Macro Score / Catalyst Confidence">Conf.</Th>
                                    <Th style={{ minWidth: '60px', textAlign: 'center' }}>View</Th>
                                </>
                            )}
                            <Th style={{ minWidth: '100px' }}>Entry</Th>
                            <Th style={{ minWidth: '100px' }}>SL</Th>
                            <Th style={{ minWidth: '100px' }}>TP</Th>
                            <Th style={{ minWidth: '90px' }}>Size</Th>
                            <Th style={{ minWidth: '100px' }}>Exit</Th>
                            <Th style={{ minWidth: '100px' }}>PnL ($)</Th>
                            <Th style={{ minWidth: '100px' }}>PnL (%)</Th>
                            {viewMode === 'detailed' && (
                                <Th style={{ minWidth: '140px' }}>Exit Reason</Th>
                            )}
                        </tr>
                    </Thead>
                    {/* Grouping Logic */}
                    <DragDropContext onDragEnd={onDragEnd}>
                        {/* Grouping Logic */}
                        {['Active', 'Ready', 'Done'].map(statusGroup => {
                            const groupTrades = filteredRows.filter(t => (t.status || 'Ready') === statusGroup).sort((a, b) => {
                                // Primary Sort: Asset (if active)
                                if (assetSortDirection) {
                                    const assetA = (a.asset || "").toString();
                                    const assetB = (b.asset || "").toString();
                                    const comparison = assetSortDirection === 'asc'
                                        ? assetA.localeCompare(assetB)
                                        : assetB.localeCompare(assetA);

                                    if (comparison !== 0) return comparison;
                                }

                                // Secondary (or Primary) Sort: Date
                                const dateA = new Date(a.date || 0);
                                const dateB = new Date(b.date || 0);
                                return dateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                            });

                            // Always render Droppable even if empty to allow dropping into empty groups
                            return (
                                <Droppable droppableId={statusGroup} key={statusGroup}>
                                    {(provided, snapshot) => (
                                        <tbody
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{
                                                background: snapshot.isDraggingOver ? (theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.05)' : 'rgba(0,0,0,0.02)') : 'transparent',
                                                transition: 'background 0.2s ease'
                                            }}
                                        >
                                            <Tr style={{ background: 'transparent' }}>
                                                <Td colSpan="100%" style={{ padding: 0, border: 'none' }}>
                                                    <SectionHeader type={statusGroup.toLowerCase()}>
                                                        {statusGroup.toUpperCase()} ({groupTrades.length})
                                                    </SectionHeader>
                                                </Td>
                                            </Tr>
                                            {groupTrades.map((trade, index) => {
                                                // We must use trade.id for draggableId to ensure uniqueness and tracking
                                                // Draggable index must be consecutive within the group (0, 1, 2...) which 'index' from map is.

                                                return (
                                                    <Draggable
                                                        key={trade.id || index}
                                                        draggableId={String(trade.id || index)}
                                                        index={index}
                                                        isDragDisabled={!isEditMode}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <Tr
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                onClick={() => handleRowClick(trade)}
                                                                style={{
                                                                    ...provided.draggableProps.style,
                                                                    background: isEditMode && selectedRows.has(trade) ? 'rgba(128, 203, 196, 0.1)' : 'transparent',
                                                                    cursor: isEditMode ? 'grab' : 'default',
                                                                    opacity: statusGroup === 'Done' ? 0.7 : 1,
                                                                    // Maintain table layout during drag
                                                                    display: snapshot.isDragging ? "table" : "table-row"
                                                                }}
                                                            >
                                                                {isEditMode && (
                                                                    <Td style={{ width: '40px', textAlign: 'center' }}>
                                                                        <CustomCheckbox
                                                                            checked={selectedRows.has(trade)}
                                                                            onChange={() => handleRowClick(trade)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </Td>
                                                                )}
                                                                {isEditMode && (
                                                                    <Td style={{ width: '50px' }}>
                                                                        <TdSmall style={{ opacity: 0.5 }}>{trade.id}</TdSmall>
                                                                    </Td>
                                                                )}
                                                                <Td><TdSmall>{isEditMode ? <EditableCell value={trade.date} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'date', val)} /> : trade.date}</TdSmall></Td>
                                                                <Td><TdSymbol>{isEditMode ? <EditableCell type="select" options={assetOptions} value={trade.asset} onChange={(val) => handleAssetChange(filteredRows.indexOf(trade), val)} /> : trade.asset}</TdSymbol></Td>
                                                                <Td>
                                                                    <TdSide type={trade.side}>
                                                                        {isEditMode ? (
                                                                            <EditableCell
                                                                                value={trade.side}
                                                                                type="select"
                                                                                options={[
                                                                                    { value: 'LONG', label: 'LONG' },
                                                                                    { value: 'SHORT', label: 'SHORT' }
                                                                                ]}
                                                                                onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'side', val)}
                                                                            />
                                                                        ) : trade.side}
                                                                    </TdSide>
                                                                </Td>

                                                                {viewMode === 'detailed' && (
                                                                    <Td>
                                                                        <TdSmall>
                                                                            {isEditMode ? (
                                                                                <EditableCell
                                                                                    type="select"
                                                                                    options={[{ value: 'MACRO', label: 'MACRO' }, { value: 'EVENT-DRIVEN', label: 'EVENT-DRIVEN' }]}
                                                                                    value={trade.style || 'MACRO'}
                                                                                    onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'style', val)}
                                                                                />
                                                                            ) : trade.style || 'MACRO'}
                                                                        </TdSmall>
                                                                    </Td>
                                                                )}
                                                                {viewMode === 'detailed' && (
                                                                    <>
                                                                        <Td>
                                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                                {isEditMode ? (
                                                                                    <>
                                                                                        <EditableCell value={trade.catalystName} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'catalystName', val)} />
                                                                                        <EditableCell value={trade.catalystType} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'catalystType', val)} />
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <span style={{ fontWeight: 600, fontSize: '11px' }}>{trade.catalystName}</span>
                                                                                        <TdSmall style={{ fontSize: '10px', opacity: 0.8 }}>{trade.catalystType}</TdSmall>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </Td>
                                                                        <Td style={{ textAlign: isEditMode ? 'left' : 'center', whiteSpace: 'normal', fontSize: '11px' }}>
                                                                            {isEditMode ? (
                                                                                <EditableCell type="textarea" value={trade.thesis} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'thesis', val)} />
                                                                            ) : (
                                                                                <BtnIcon
                                                                                    onClick={(e) => { e.stopPropagation(); setActiveThesisTrade(trade); }}
                                                                                    title={trade.thesis ? "View Thesis" : "No Thesis"}
                                                                                    style={{ color: trade.thesis ? undefined : 'rgba(128,128,128,0.3)' }}
                                                                                >
                                                                                    <FiEye style={{ width: '16px', height: '16px' }} />
                                                                                </BtnIcon>
                                                                            )}
                                                                        </Td>
                                                                        <Td>
                                                                            <TdSide type={trade.scenario === 'Bullish' ? 'LONG' : trade.scenario === 'Bearish' ? 'SHORT' : 'NEUTRAL'}>
                                                                                {isEditMode ? <EditableCell value={trade.scenario} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'scenario', val)} /> : trade.scenario}
                                                                            </TdSide>
                                                                        </Td>
                                                                        <Td style={{ textAlign: 'center' }}>
                                                                            <TdSmall>
                                                                                {isEditMode ? (
                                                                                    <EditableCell
                                                                                        value={getEffectiveConfidence(trade) !== null && getEffectiveConfidence(trade) !== undefined ? getEffectiveConfidence(trade) : ''}
                                                                                        onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'confidenceScore', val)}
                                                                                    />
                                                                                ) : (
                                                                                    renderConfidenceBadge(getEffectiveConfidence(trade), trade.style)
                                                                                )}
                                                                            </TdSmall>
                                                                        </Td>
                                                                        <Td style={{ textAlign: 'center' }}>
                                                                             <BtnIcon
                                                                                 onClick={(e) => { e.stopPropagation(); setActiveAnalysisTrade(trade); }}
                                                                                 title="View Setup"
                                                                                 style={{ color: trade.view ? undefined : 'rgba(128,128,128,0.3)' }}
                                                                             >
                                                                                 <FiEye style={{ width: '16px', height: '16px' }} />
                                                                             </BtnIcon>
                                                                         </Td>
                                                                     </>
                                                                 )}
                                                                 <Td>{isEditMode ? <EditableCell value={trade.entry} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'entry', val)} /> : trade.entry}</Td>
                                                                 <Td style={{ color: '#ef5350' }}>{isEditMode ? <EditableCell value={trade.sl} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'sl', val)} /> : trade.sl}</Td>
                                                                 <Td style={{ color: '#66bb6a' }}>{isEditMode ? <EditableCell value={trade.tp} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'tp', val)} /> : trade.tp}</Td>
                                                                 <Td>{isEditMode ? <EditableCell value={trade.size} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'size', val)} /> : trade.size}</Td>
                                                                 <Td>{isEditMode ? <EditableCell value={trade.exit} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'exit', val)} /> : trade.exit}</Td>
                                                                 <Td><TdPnl positive={trade.pnl ? trade.pnl.includes('+') : false}>{isEditMode ? <EditableCell value={trade.pnl} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'pnl', val)} /> : trade.pnl}</TdPnl></Td>
                                                                 <Td><TdPnl positive={trade.pnlPercent ? trade.pnlPercent.includes('+') : false}>{isEditMode ? <EditableCell value={trade.pnlPercent} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'pnlPercent', val)} /> : trade.pnlPercent}</TdPnl></Td>
                                                                 {viewMode === 'detailed' && (
                                                                     <Td><TdSmall>{isEditMode ? <EditableCell type="select" options={exitReasonOptions} value={trade.exitReason} onChange={(val) => handleCellUpdate(filteredRows.indexOf(trade), 'exitReason', val)} /> : trade.exitReason}</TdSmall></Td>
                                                                 )}
                                                            </Tr>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </tbody>
                                    )}
                                </Droppable>
                            );
                        })}
                    </DragDropContext>
                    {/* Closing tbody here instead of inside droppable since we removed DragDrop for grouped view */}
                </Table>

            </TableWrapper>

            {
                showScrollX && (
                    <ScrollRail ref={scrollRailXRef}>
                        <ScrollThumb
                            width={thumbWidth}
                            left={thumbLeft}
                            onMouseDown={handleMouseDownX}
                        />
                    </ScrollRail>
                )
            }

            {
                showScrollY && (
                    <ScrollRailVertical ref={scrollRailYRef}>
                        <ScrollThumbVertical
                            height={thumbHeight}
                            top={thumbTop}
                            onMouseDown={handleMouseDownY}
                        />
                    </ScrollRailVertical>
                )
            }

            {
                rowsToDelete && (
                    <ModalOverlay>
                        <ModalContent>
                            <h3 style={{ color: theme.palette.mode === 'dark' ? '#fff' : colors.grey[100], marginTop: 0 }}>Confirm Deletion</h3>
                            <p style={{ color: theme.palette.mode === 'dark' ? '#b0bec5' : colors.grey[300] }}>
                                Are you sure you want to delete {rowsToDelete.length} row{rowsToDelete.length > 1 ? 's' : ''}?
                                This action cannot be undone.
                            </p>
                            <ButtonRow>
                                <Btn onClick={() => setRowsToDelete(null)}>Cancel</Btn>
                                <Btn primary onClick={confirmDelete} style={{ background: '#ef5350', borderColor: '#ef5350' }}>Delete</Btn>
                            </ButtonRow>
                        </ModalContent>
                    </ModalOverlay>
                )
            }

            {activeAnalysisTrade && (
                <ModalOverlay onClick={() => setActiveAnalysisTrade(null)}>
                    <ModalContent style={{ width: '600px', maxWidth: '90vw', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: colors.grey[100] }}>View</h3>
                        </div>
                        <TreeContainer>
                            <TreeView
                                data={(() => {
                                    try {
                                        return activeAnalysisTrade.view ? JSON.parse(activeAnalysisTrade.view) : {};
                                    } catch (e) {
                                        return { "Error": "Invalid JSON data" };
                                    }
                                })()}
                                isRoot={true}
                            />
                        </TreeContainer>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                            <Btn onClick={() => setActiveAnalysisTrade(null)} title="Close">
                                <MdClose size={18} />
                            </Btn>
                            <Btn
                                primary
                                onClick={() => {
                                    const data = activeAnalysisTrade.view ? JSON.parse(activeAnalysisTrade.view) : {};
                                    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                                }}
                                title="Copy JSON"
                            >
                                <MdContentCopy size={18} />
                            </Btn>
                        </div>
                    </ModalContent>
                </ModalOverlay>
            )}

            {activeThesisTrade && (
                <ModalOverlay onClick={() => setActiveThesisTrade(null)}>
                    <ModalContent style={{ width: '450px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: colors.grey[100] }}>Thesis</h3>
                        </div>

                        {isEditMode ? (
                            <textarea
                                value={activeThesisTrade.thesis || ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const idx = filteredRows.indexOf(activeThesisTrade);
                                    if (idx !== -1) {
                                        handleCellUpdate(idx, 'thesis', val);
                                    }
                                    setActiveThesisTrade(prev => ({ ...prev, thesis: val }));
                                }}
                                style={{
                                    width: '100%',
                                    height: '180px',
                                    background: colors.grey[900],
                                    color: colors.grey[100],
                                    border: `1px solid ${colors.grey[700]}`,
                                    borderRadius: '6px',
                                    padding: '12px',
                                    fontSize: '0.85rem',
                                    lineHeight: '1.6',
                                    resize: 'vertical',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit'
                                }}
                                placeholder="Write your trade thesis..."
                            />
                        ) : (
                            <div style={{
                                padding: '14px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                lineHeight: '1.6',
                                color: colors.grey[100],
                                whiteSpace: 'pre-wrap',
                                maxHeight: '350px',
                                overflowY: 'auto'
                            }}>
                                {activeThesisTrade.thesis || <span style={{ color: colors.grey[500], fontStyle: 'italic' }}>No thesis provided.</span>}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                            <Btn onClick={() => setActiveThesisTrade(null)} title="Close">
                                <MdClose size={18} />
                            </Btn>
                            <Btn
                                primary
                                onClick={() => {
                                    navigator.clipboard.writeText(activeThesisTrade.thesis || '');
                                }}
                                title="Copy Thesis"
                            >
                                <MdContentCopy size={18} />
                            </Btn>
                        </div>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Panel >
    );
};

export default JournalTab;
